import { useState, useEffect, useCallback } from 'react';

interface OfflineAction {
  id?: number;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  timestamp: number;
}

export function useOfflineMode() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSlow, setIsSlow] = useState(false);
  const [pendingActions, setPendingActions] = useState<OfflineAction[]>([]);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);

  // Register service worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
          setServiceWorkerReady(true);

          // Request background sync permission
          if ('sync' in registration) {
            console.log('Background Sync is supported');
          }
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network status: Online');
      setIsOnline(true);

      // Trigger background sync when coming back online
      if ('serviceWorker' in navigator && 'sync' in navigator.serviceWorker) {
        navigator.serviceWorker.ready.then((registration: any) => {
          return registration.sync.register('sync-offline-data');
        });
      }
    };

    const handleOffline = () => {
      console.log('Browser says OFFLINE');
      setIsOnline(false);
      setIsSlow(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Proactive health-check: measures round-trip time to classify slow connections.
  // Only runs while the browser believes it is online (avoids redundant checks).
  const checkNetworkSpeed = useCallback(async () => {
  const start = Date.now();

  try {
    const response = await fetch('/health?nocache=' + Date.now(), {
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error('Network response not ok');
    }

    const duration = Date.now() - start;

    if (!navigator.onLine) {
      console.log("🚫 Browser still offline, ignoring response");
      return;
    }

    setIsOnline(true);
    setIsSlow(duration > 2000);

  } catch (error) {
    console.log("🚫 Still offline");

    setIsOnline(false);
    setIsSlow(false);
  }
}, []);

  // Load pending actions from IndexedDB
  const loadPendingActions = useCallback(async () => {
    try {
      const db = await openDatabase();
      const actions = await getAllActions(db);
      setPendingActions(actions);
    } catch (error) {
      console.error('Failed to load pending actions:', error);
    }
  }, []);

  // Queue action for offline sync
  const queueAction = useCallback(
    async (action: Omit<OfflineAction, 'timestamp' | 'id'>) => {
      try {
        const db = await openDatabase();
        const actionWithTimestamp: OfflineAction = {
          ...action,
          timestamp: Date.now(),
        };

        await addAction(db, actionWithTimestamp);
        await loadPendingActions();

        console.log('Action queued for offline sync:', action);
      } catch (error) {
        console.error('Failed to queue action:', error);
      }
    },
    [loadPendingActions]
  );

  // Clear all pending actions
  const clearPendingActions = useCallback(async () => {
    try {
      const db = await openDatabase();
      await clearAllActions(db);
      setPendingActions([]);
    } catch (error) {
      console.error('Failed to clear pending actions:', error);
    }
  }, []);

  // Load pending actions on mount
  useEffect(() => {
    loadPendingActions();
  }, [loadPendingActions]);

  // Periodic health check every 5 seconds while online
  useEffect(() => {
    const interval = setInterval(() => {
      {
        checkNetworkSpeed();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [checkNetworkSpeed]);

  return {
    isOnline,
    isSlow,
    serviceWorkerReady,
    pendingActions,
    queueAction,
    clearPendingActions,
    loadPendingActions,
  };
}

// IndexedDB helper functions
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('ambient-ai-offline', 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('actions')) {
        db.createObjectStore('actions', { keyPath: 'id', autoIncrement: true });
      }
    };
  });
}

function getAllActions(db: IDBDatabase): Promise<OfflineAction[]> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['actions'], 'readonly');
    const store = transaction.objectStore('actions');
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

function addAction(db: IDBDatabase, action: OfflineAction): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['actions'], 'readwrite');
    const store = transaction.objectStore('actions');
    const request = store.add(action);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

function clearAllActions(db: IDBDatabase): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['actions'], 'readwrite');
    const store = transaction.objectStore('actions');
    const request = store.clear();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}
